import type { Team } from "@fscrypto/domain/team";
import type { User } from "@fscrypto/domain/user";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ChevronDown, MenuIcon, MoveUpRight } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/accordion/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/avatar/avatar";
import { Button } from "../../components/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/dropdown-menu/dropdown-menu";
import { TrackedLink } from "../../components/tracked-click/TrackedLink";
import { CareersIcon } from "../../icons/careers-icon";
import { CaseStudiesIcon } from "../../icons/case-studies-icon";
import { ChartIcon } from "../../icons/chart-icon";
import { DocsIcon } from "../../icons/docs-icon";
import FlipsideIcon from "../../icons/flipside-icon";
import { InsightsIcon } from "../../icons/insights-icon";
import { JourneysIcon } from "../../icons/journeys-icon";
import { PlatformIcon } from "../../icons/platform-icon";
import { PlaybookIcon } from "../../icons/playbook-icon";
import { StudioIcon } from "../../icons/studio-icon";
import { LogoIcon } from "../../primitives/logo";
import { Text } from "../../primitives/text/text";
import { cn } from "../../utils/cn";
import ConnectWallet from "../connect-wallet/connect-wallet";
import { ContactUsDialog } from "../contact-us-dialog/contact-us-dialog";
import { useReward } from "./use-reward";

interface HeaderProps {
  renderMenuContent?: React.ReactNode;
  authMenuContent?: React.ReactNode;
  user?: User | null;
  team?: Team | null;
  dark?: boolean;
  transparent?: boolean;
  tracking?: (event: string) => void;
  onLogout?: () => void;
  connectWalletConfig?: {
    dynamicEnvironmentId: string;
    myNearWalletCallbackUrl: string;
  };
  contactUsConfig?: {
    utmCampaign?: string;
    utmSource?: string;
    utmMedium?: string;
  };
  showInsightsNavItem?: boolean;
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({
  renderMenuContent,
  authMenuContent,
  user,
  team,
  dark,
  transparent,
  tracking,
  onLogout,
  connectWalletConfig,
  showInsightsNavItem = false,
  className,
  contactUsConfig,
}) => {
  return (
    <header
      className={cn(
        "bg-bg-primary w-full relative z-30 bg-bg-black py-6 md:py-4",
        {
          dark: dark,
          "bg-transparent": !!transparent,
          "bg-bg-primary": !transparent,
        },
        className,
      )}
    >
      <div className="w-full justify-between px-10 hidden min-[1090px]:flex">
        {showInsightsNavItem ? (
          <div className="flex items-center space-x-16">
            <HeaderLogo />
            <HeaderLeftNav tracking={tracking} showInsightsNavItem={showInsightsNavItem} dark={dark} />
          </div>
        ) : (
          <div className="flex items-center space-x-8">
            <HeaderLogo />
            <HeaderLeftNav tracking={tracking} showInsightsNavItem={showInsightsNavItem} dark={dark} />
          </div>
        )}

        <div className="flex items-center">
          <HeaderRightNav tracking={tracking} showInsightsNavItem={showInsightsNavItem} />
          <HeaderAuth
            renderMenuContent={renderMenuContent}
            authMenuContent={authMenuContent}
            user={user}
            team={team}
            tracking={tracking}
            onLogout={onLogout}
            connectWalletConfig={connectWalletConfig}
            contactUsConfig={contactUsConfig}
            dark={dark}
          />
        </div>
      </div>
      <MobileNav
        user={user}
        connectWalletConfig={connectWalletConfig}
        showInsightsNavItem={showInsightsNavItem}
        dark={dark}
      />
    </header>
  );
};

const HeaderLogo: React.FC = () => (
  <a href="/" aria-label="Flipside" className="flex items-center space-x-2">
    <LogoIcon className="h-8 w-auto" />
    <h1 className="text-xl">flipside</h1>
  </a>
);

const HeaderLeftNav: React.FC<{
  tracking?: (event: string) => void;
  showInsightsNavItem?: boolean;
  dark?: boolean;
}> = ({ tracking, dark }) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  return (
    <nav className="hidden space-x-4 md:flex">
      <HoverDropdownMenu
        dark={dark}
        open={openMenu === "products"}
        setOpen={() => setOpenMenu("products")}
        close={() => setOpenMenu(null)}
        label="Products"
      >
        <ProductsMenuContent tracking={tracking} dark={dark} />
      </HoverDropdownMenu>

      <HoverDropdownMenu
        dark={dark}
        open={openMenu === "protocols"}
        setOpen={() => setOpenMenu("protocols")}
        close={() => setOpenMenu(null)}
        label="For Protocols"
      >
        <ProtocolsMenuContent tracking={tracking} dark={dark} />
      </HoverDropdownMenu>

      <HoverDropdownMenu
        dark={dark}
        open={openMenu === "analysts"}
        setOpen={() => setOpenMenu("analysts")}
        close={() => setOpenMenu(null)}
        label="For Analysts"
      >
        <AnalystsMenuContent tracking={tracking} dark={dark} />
      </HoverDropdownMenu>

      <HoverDropdownMenu
        dark={dark}
        open={openMenu === "explorers"}
        setOpen={() => setOpenMenu("explorers")}
        close={() => setOpenMenu(null)}
        label="For Explorers"
      >
        <ExplorersMenuContent tracking={tracking} dark={dark} />
      </HoverDropdownMenu>

      <HoverDropdownMenu
        dark={dark}
        open={openMenu === "about"}
        setOpen={() => setOpenMenu("about")}
        close={() => setOpenMenu(null)}
        label="About"
      >
        <AboutMenuContent tracking={tracking} dark={dark} />
      </HoverDropdownMenu>
    </nav>
  );
};

export const HoverDropdownMenu = ({
  className,
  dark,
  open,
  setOpen,
  close,
  children,
  label,
}: {
  className?: string;
  dark?: boolean;
  open: boolean;
  setOpen: () => void;
  close: () => void;
  children: React.ReactNode;
  label: string;
}) => {
  return (
    <DropdownMenu open={open} onOpenChange={(isOpen) => (isOpen ? setOpen() : close())} modal={false}>
      <DropdownMenuTrigger>
        <div
          className={cn("flex items-center space-x-1 px-1 cursor-pointer", className)}
          onMouseEnter={() => setOpen()}
        >
          <span className="text-xs font-medium uppercase tracking-wider font-aeonikMono cursor-pointer">{label}</span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="mt-4"
        dark={dark}
        onMouseLeave={close}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const HeaderRightNav: React.FC<{ tracking?: (event: string) => void; showInsightsNavItem?: boolean }> = ({
  tracking,
  showInsightsNavItem,
}) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isEarn = pathname.startsWith("/earn");

  return (
    <nav
      className={`hidden space-x-4 md:flex justify-between items-center flex-1 max-w-[600px] ${showInsightsNavItem ? "max-w-[550px]" : ""}`}
    >
      {showInsightsNavItem ? (
        <div className="flex items-center mr-6">
          <HeaderNavItem href="/studio" onClick={tracking && (() => tracking("nav_click_studio"))} className="mr-2">
            <Button
              variant="outline"
              className={cn("border-gray-400 dark:border-primary hover:bg-primary/20", {
                "border-primary": isEarn,
              })}
            >
              Studio
              <MoveUpRight className="size-4 ml-2" />
            </Button>
          </HeaderNavItem>
          <HeaderNavItem href="/earn/" onClick={tracking && (() => tracking("nav_click_quests"))}>
            <Button
              variant="outline"
              className={cn("border-gray-400 dark:border-primary hover:bg-primary/20", {
                "border-primary": isEarn,
              })}
            >
              Journeys
              <MoveUpRight className="size-4 ml-2" />
            </Button>
          </HeaderNavItem>
        </div>
      ) : null}
    </nav>
  );
};

interface HeaderNavItemProps {
  href: string;
  children: React.ReactNode;
  onClick?: (() => void) | undefined;
  className?: string;
}

const HeaderNavItem: React.FC<HeaderNavItemProps> = ({ href, children, onClick, className }) => (
  <TrackedLink href={href} className={cn("text-sm block px-2 relative font-medium", className)} onClick={onClick}>
    {children}
  </TrackedLink>
);

// REWARDS COUNT /////////////////////////////////////////////////////////

const HeaderRewardsCountNavItem = () => {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <HeaderNavItem href="/earn/rewards">
        <HeaderRewardsCountInner />
      </HeaderNavItem>
    </QueryClientProvider>
  );
};

const HeaderRewardsCountInner = () => {
  const { rewardsCount } = useReward();
  return (
    <>
      <span className="font-aeonikMono">REWARDS</span>
      {rewardsCount !== undefined && rewardsCount > 0 && (
        <div className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 transform rounded-full bg-red-500 px-1 text-xs text-white font-aeonikMono">
          {rewardsCount}
        </div>
      )}
    </>
  );
};

// AUTH //////////////////////////////////////////////////////////////////

const HeaderAuth: React.FC<HeaderProps> = ({
  team,
  renderMenuContent,
  authMenuContent,
  user,
  onLogout,
  dark,
  connectWalletConfig,
  contactUsConfig,
  tracking,
}) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isEarn = pathname.startsWith("/earn");
  return (
    <div className="flex items-center space-x-4">
      {authMenuContent && <div className="ml-2">{authMenuContent}</div>}
      {user ? (
        <>
          <div className="flex items-center justify-around mx-2">
            <a href="/studio" className="mx-1 hidden md:block w-1/2">
              <Button
                variant="outline"
                className={cn("border-black w-1/2 min-w-full max-w-full dark:border-primary", {
                  "border-primary": isEarn,
                })}
                font="mono"
              >
                Studio
                <MoveUpRight className="size-4 ml-2" />
              </Button>
            </a>
            <a href="/earn/" className="mx-1 block w-1/2 flex-grow">
              <Button
                variant="outline"
                className={cn("border-black w-1/2 min-w-full max-w-full dark:border-primary", {
                  "border-primary": isEarn,
                })}
                font="mono"
              >
                Journeys
                <MoveUpRight className="size-4 ml-2" />
              </Button>
            </a>
          </div>
          <HeaderRewardsCountNavItem />
          <UserMenu
            user={user}
            team={team}
            renderMenuContent={renderMenuContent}
            onLogout={onLogout}
            dark={dark}
            connectWalletConfig={connectWalletConfig}
          />
        </>
      ) : (
        <>
          <HeaderAuthButton
            variant="ghost"
            href="/home/login"
            onClick={tracking ? () => tracking("nav_click_login") : undefined}
          >
            Log in
          </HeaderAuthButton>
          {/* div necessary for space-x-* */}
          <div>
            <ContactUsDialog
              messagePrompt="WHAT CAN WE HELP SOLVE?"
              tracking={tracking ? () => tracking : undefined}
              defaultValues={contactUsConfig}
            >
              <Button
                variant="default"
                font="mono"
                onClick={tracking ? () => tracking("nav_click_become_a_partner") : undefined}
              >
                BECOME A PARTNER
              </Button>
            </ContactUsDialog>
          </div>
        </>
      )}
    </div>
  );
};

interface HeaderAuthButtonProps {
  variant: "default" | "ghost" | "outline";
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (() => void) | undefined;
}

const HeaderAuthButton: React.FC<HeaderAuthButtonProps> = ({ variant, href, children, className, onClick }) => (
  <TrackedLink href={href} onClick={onClick}>
    <Button variant={variant} className={className} font="mono">
      {children}
    </Button>
  </TrackedLink>
);

// USER MENU //////////////////////////////////////////////////////////////////

interface UserMenuProps {
  renderMenuContent?: React.ReactNode;
  user: User;
  team?: Team | null;
  onLogout?: () => void;
  dark?: boolean;
  connectWalletConfig?: HeaderProps["connectWalletConfig"];
}

const UserMenu: React.FC<UserMenuProps> = ({ user, team, renderMenuContent, dark, connectWalletConfig, onLogout }) => {
  const [openWalletModal, setOpenWalletModal] = useState(false);

  return (
    <DropdownMenu>
      {connectWalletConfig && (
        <ConnectWallet
          config={{
            dynamicEnvironmentId: connectWalletConfig.dynamicEnvironmentId,
            myNearWalletCallbackUrl: connectWalletConfig.myNearWalletCallbackUrl,
            theme: "dark",
          }}
          controlled={{
            open: openWalletModal,
            setOpen: setOpenWalletModal,
          }}
        />
      )}
      <DropdownMenuTrigger asChild>
        <div className="items-center cursor-pointer flex space-x-1">
          <Avatar className="size-6">
            <AvatarImage src={user.avatarUrl} alt={user.username} />
            <AvatarFallback>{user.username.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          {team && (
            <div className="bg-bg-tertiary/50 p-1 pr-3 rounded-full flex items-center space-x-2 text-sm">
              <Avatar className="size-6">
                <AvatarImage src={team.avatarUrl} />
                <AvatarFallback>{team.name.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span>{team.name}</span>
            </div>
          )}
          <ChevronDown className="size-4 stroke-1" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("w-60", dark && "dark")}>
        <div className="px-3 py-2 text-sm font-normal">
          <div className="flex flex-col space-y-1">
            <p>{user.username}</p>
            <p className="text-text-tertiary">{user.email}</p>
          </div>
        </div>
        {renderMenuContent && (
          <>
            <DropdownMenuSeparator />
            {renderMenuContent}
          </>
        )}
        <DropdownMenuSeparator />
        {team && (
          <DropdownMenuItem asChild>
            <a className="h-full w-full cursor-pointer" href={`/${team.slug}`}>
              Team Profile
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a className="h-full w-full cursor-pointer" href={`/${user.username}`}>
            Profile
          </a>
        </DropdownMenuItem>
        {connectWalletConfig && (
          <DropdownMenuItem asChild>
            <span
              className="h-full w-full cursor-pointer"
              onClick={() => {
                setTimeout(() => {
                  setOpenWalletModal(true);
                }, 300);
              }}
            >
              Wallets
            </span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a className="h-full w-full cursor-pointer" href="/settings/plan">
            Settings
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a className="h-full w-full cursor-pointer" href="/earn/rewards">
            Rewards
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a className="h-full w-full cursor-pointer" href="/earn/history">
            Quest History
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <button
            type="submit"
            className="h-full w-full cursor-pointer text-left"
            onClick={() => {
              onLogout?.();
              window.location.href = `${window.location.origin}/home/logout`;
            }}
          >
            Log out
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ABOUT MENU //////////////////////////////////////////////////////////////////

const AboutMenuContent = ({ tracking, dark }: { tracking?: (event: string) => void; dark?: boolean }) => (
  <>
    <DropdownMenuItem>
      <a href="/home/about" onClick={tracking && (() => tracking("nav_click_company"))} className="flex items-center">
        <FlipsideIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Company</Text>
          <Text variant="caption" className="text-text-tertiary">
            Learn about Flipside's mission, values, and products
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a
        href="https://flipsidecrypto.xyz/fc/resources"
        target="_blank"
        onClick={tracking && (() => tracking("nav_click_resources"))}
        rel="noreferrer"
        className="flex items-center"
      >
        <CaseStudiesIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Resources</Text>
          <Text variant="caption" className="text-text-tertiary">
            Tools, guides, research, reports, and beyond
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a
        href="https://flipsidecrypto.xyz/home/careers"
        target="_blank"
        onClick={tracking && (() => tracking("nav_click_careers"))}
        rel="noreferrer"
        className="flex items-center"
      >
        <CareersIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Careers</Text>
          <Text variant="caption" className="text-text-tertiary">
            Join the scientists pioneering blockchain growth
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a
        href="https://docs.flipsidecrypto.xyz/"
        target="_blank"
        onClick={tracking && (() => tracking("nav_click_documentation"))}
        rel="noreferrer"
        className="flex items-center"
      >
        <DocsIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Documentation</Text>
          <Text variant="caption" className="text-text-tertiary">
            Explore docs to get the most out of Flipside
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
  </>
);

// PRODUCTS MENU //////////////////////////////////////////////////////////////

const ProductsMenuContent = ({ tracking, dark }: { tracking?: (event: string) => void; dark?: boolean }) => (
  <>
    <DropdownMenuItem>
      <a
        href="/insights/dashboards"
        onClick={tracking && (() => tracking("nav_click_insights"))}
        className="flex items-center"
      >
        <InsightsIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Insights</Text>
          <Text variant="caption" className="text-text-tertiary">
            Explore trending dashboards, queries, and analysts
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a href="/studio" onClick={tracking && (() => tracking("nav_click_studio"))} className="flex items-center">
        <StudioIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Studio</Text>
          <Text variant="caption" className="text-text-tertiary">
            Create dashboards and queries
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a href="/earn" onClick={tracking && (() => tracking("nav_click_earn"))} className="flex items-center">
        <JourneysIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Journeys</Text>
          <Text variant="caption" className="text-text-tertiary">
            Earn rewards for navigating onchain activities
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
  </>
);

// PROTOCOLS MENU //////////////////////////////////////////////////////////////

const ProtocolsMenuContent = ({ tracking, dark }: { tracking?: (event: string) => void; dark?: boolean }) => (
  <>
    <DropdownMenuItem>
      <a
        href="/home/platform"
        onClick={tracking && (() => tracking("nav_click_platform"))}
        className="flex items-center"
      >
        <PlatformIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Flipside Platform</Text>
          <Text variant="caption" className="text-text-tertiary">
            Explore the leading Intelligence-Driven Growth platform
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a
        href="/home/playbook"
        onClick={tracking && (() => tracking("nav_click_playbook"))}
        className="flex items-center"
      >
        <PlaybookIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">IDG Playbook</Text>
          <Text variant="caption" className="text-text-tertiary">
            Discover the Intelligence-Driven Growth formula
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      {/* biome-ignore lint/a11y/useValidAnchor: ? */}
      <a
        href="/fc/resources#case-studies"
        onClick={tracking && (() => tracking("nav_click_earn"))}
        className="flex items-center"
      >
        <CaseStudiesIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Case Studies</Text>
          <Text variant="caption" className="text-text-tertiary">
            Learn how blockchain partners achieve growth
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem className="focus:bg-inherit">
      <div className="flex flex-col min-[1090px]:flex-row justify-evenly w-full gap-2">
        <a href="/home/playbook">
          <Button
            className="w-full px-20"
            variant="default"
            font="mono"
            onClick={() => tracking && (() => tracking("nav_click_playbook"))}
          >
            Download Playbook
          </Button>
        </a>
      </div>
    </DropdownMenuItem>
  </>
);

// ANALYSTS MENU //////////////////////////////////////////////////////////////

const AnalystsMenuContent = ({ tracking, dark }: { tracking?: (event: string) => void; dark?: boolean }) => (
  <>
    <DropdownMenuItem>
      <a
        href="/home/analysts"
        onClick={tracking && (() => tracking("nav_click_analysts"))}
        className="flex items-center"
      >
        <ChartIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Flipside for analysts</Text>
          <Text variant="caption" className="text-text-tertiary">
            Access free data from leading blockchains
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem>
      <a
        href="/insights/dashboards"
        onClick={tracking && (() => tracking("nav_click_insights"))}
        className="flex items-center"
      >
        <InsightsIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Insights</Text>
          <Text variant="caption" className="text-text-tertiary">
            Explore trending dashboards, queries, and analysts
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem className="focus:bg-inherit">
      <a href="/studio" className="w-full flex justify-center">
        <Button className="w-full px-20" font="mono">
          Launch Studio
        </Button>
      </a>
    </DropdownMenuItem>
  </>
);

// Explorers Menu /////////////////////////////////////////////////////////////

const ExplorersMenuContent = ({ tracking, dark }: { tracking?: (event: string) => void; dark?: boolean }) => (
  <>
    <DropdownMenuItem>
      <a
        href="/home/explorers"
        onClick={tracking && (() => tracking("nav_click_analysts"))}
        className="flex items-center"
      >
        <JourneysIcon className="size-5 mr-2" dark={dark} />
        <div className="flex flex-col space-y-1">
          <Text variant="button">Flipside for explorers</Text>
          <Text variant="caption" className="text-text-tertiary">
            Earn rewards for navigating onchain activities
          </Text>
        </div>
      </a>
    </DropdownMenuItem>
    <DropdownMenuItem className="focus:bg-inherit">
      <a href="/earn/" className="w-full flex justify-center">
        <Button className="w-full px-20" font="mono">
          Start a Journey
        </Button>
      </a>
    </DropdownMenuItem>
  </>
);

// MOBILE MENU ///////////////////////////////////////////////////////////////

interface MobileMenuProps {
  user?: User | null;
  connectWalletConfig?: HeaderProps["connectWalletConfig"];
  showInsightsNavItem?: boolean;
  dark?: boolean;
}

const MobileNav: React.FC<MobileMenuProps> = ({ user, connectWalletConfig, showInsightsNavItem = false, dark }) => (
  <div className="min-[1090px]:hidden w-full flex items-center justify-between px-4">
    <HeaderLogo />
    <div className="flex items-center space-x-1">
      <MobileMenu user={user} showInsightsNavItem={showInsightsNavItem} dark={dark} />
      {user && <UserMenu user={user} connectWalletConfig={connectWalletConfig} />}
    </div>
  </div>
);

const MobileMenu: React.FC<MobileMenuProps> = ({ user, dark }) => {
  const [contactUsOpen, setContactUsOpen] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <>
      <ContactUsDialog messagePrompt="WHAT CAN WE HELP SOLVE?" open={contactUsOpen} setOpen={setContactUsOpen} />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost">
            <MenuIcon className="h-6 w-6" /> {/* Use MenuIcon as the trigger */}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex flex-col justify-between p-0 pt-2 w-80 mt-4" dark={dark}>
          <div className="flex flex-col pb-2">
            <Accordion type="multiple" defaultValue={["item-1"]} className="px-3 ml-4">
              <AccordionItem value="item-1" className="border-none">
                <AccordionTrigger className="!no-underline py-2">
                  <Text variant="button" font="mono" className="uppercase">
                    Products
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <ProductsMenuContent dark={dark} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-none">
                <AccordionTrigger className="!no-underline py-2">
                  <Text variant="button" font="mono" className="uppercase">
                    For Protocols
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <ProtocolsMenuContent dark={dark} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-none">
                <AccordionTrigger className="!no-underline py-2">
                  <Text variant="button" font="mono" className="uppercase">
                    For Analysts
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <AnalystsMenuContent dark={dark} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-none">
                <AccordionTrigger className="!no-underline py-2 !text-xs">
                  <Text variant="button" font="mono" className="uppercase">
                    For Explorers
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <ExplorersMenuContent dark={dark} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-none">
                <AccordionTrigger className="!no-underline py-2 !text-xs">
                  <Text variant="button" font="mono" className="uppercase">
                    About
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <AboutMenuContent dark={dark} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          {!user && (
            <div className="flex p-4 gap-x-4">
              <>
                <HeaderAuthButton variant="outline" href="/home/login" className="w-full max-w-full">
                  Log in
                </HeaderAuthButton>
                <div>
                  <Button
                    variant="default"
                    font="mono"
                    onClick={() => {
                      setContactUsOpen(true);
                      setOpen(false);
                    }}
                  >
                    BECOME A PARTNER
                  </Button>
                </div>
              </>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
